import React, { useEffect, useState } from 'react';
import { Pressable, View } from 'react-native';
import { Link } from 'expo-router';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import TutorialVideo from '@/components/Tutorials/TutorialVideo';
import { getTutorialVideos } from '@/lib/tutorialVideos';
import { useLanding } from './LandingKit';
import { prefersReducedMotion } from './landingTokens';

/** How long a category stays in front before the showcase advances. */
const ROTATE_MS = 8000;

const EXAMPLES = [
  { id: 'presentation', label: 'A presentation', icon: 'file-powerpoint', lesson: 'work-presentations' },
  { id: 'document', label: 'A document', icon: 'file-lines', lesson: 'work-documents' },
  { id: 'spreadsheet', label: 'A spreadsheet', icon: 'table', lesson: 'work-spreadsheets' },
  { id: 'video', label: 'A video', icon: 'film', lesson: 'work-videos' },
  { id: 'code', label: 'A coding project', icon: 'code', lesson: 'code-help' },
  { id: 'game', label: 'A video game', icon: 'gamepad', lesson: 'work-games' },
  { id: 'song', label: 'A song', icon: 'music', lesson: 'music-generation' },
  { id: 'image', label: 'An image', icon: 'image', lesson: 'work-images' },
  { id: 'research', label: 'Research', icon: 'magnifying-glass', lesson: 'research' },
];

function HeroProductPreview() {
  const { c, width } = useLanding();
  const narrow = width < 430;
  const [selected, setSelected] = useState('presentation');
  const [rotating, setRotating] = useState(() => !prefersReducedMotion());
  const example = EXAMPLES.find(item => item.id === selected);
  const [video] = getTutorialVideos('learn', example.lesson);
  const lessonHref = `/docs/learn/${example.lesson}`;

  /* The categories cycle while nothing is playing: a pick restarts the timer
   * for the chosen example, and pressing play stops the rotation for good so
   * the watched video — and its end card — are never swapped away. */
  useEffect(() => {
    if (!rotating) return undefined;
    const index = EXAMPLES.findIndex(item => item.id === selected);
    const timer = setTimeout(() => {
      setSelected(EXAMPLES[(index + 1) % EXAMPLES.length].id);
    }, ROTATE_MS);
    return () => clearTimeout(timer);
  }, [rotating, selected]);

  return (
    <View style={{ gap: 28, width: '100%', maxWidth: 640, alignSelf: 'center' }}>
      <View accessibilityRole="group" accessibilityLabel="Explore what you can create" style={{ flexDirection: 'row', flexWrap: 'wrap', alignSelf: 'center', width: '100%', maxWidth: 600, padding: 4, rowGap: 2, borderRadius: 22, backgroundColor: c.panel, borderWidth: 1, borderColor: c.rule }}>
        {EXAMPLES.map(item => (
          <Pressable
            key={item.id}
            accessibilityRole="button"
            accessibilityLabel={`Watch ${item.label.toLowerCase()}`}
            aria-pressed={item.id === selected}
            aria-controls="landing-product-example"
            dataSet={{ landingPreviewChoice: 'true' }}
            onPress={() => setSelected(item.id)}
            style={{ width: '33.333333%', minWidth: 0, minHeight: 44, paddingHorizontal: narrow ? 4 : 8, borderRadius: 999, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 8, backgroundColor: item.id === selected ? c.cardSolid : 'transparent', cursor: 'pointer', boxShadow: item.id === selected ? c.shadowSoft : 'none' }}
          >
            {!narrow && <Icon name={item.icon} size={13} color={item.id === selected ? c.ink : c.muted} />}
            <Text weight={item.id === selected ? 'semibold' : 'medium'} style={{ flexShrink: 1, textAlign: 'center', fontSize: narrow ? 11 : 13, color: item.id === selected ? c.ink : c.inkSoft }}>{item.label}</Text>
          </Pressable>
        ))}
      </View>
      <View nativeID="landing-product-example" accessibilityRole="region" accessibilityLabel={`${example.label} video`} style={{ gap: 18 }}>
        <View style={{ gap: 8 }}>
          <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: narrow ? 21 : 25, lineHeight: narrow ? 27 : 32, letterSpacing: -0.5 }}>{video.title}</Text>
          <Text tag="p" style={{ color: c.inkSoft, fontSize: 15, lineHeight: 24, maxWidth: 680 }}>{video.description}</Text>
        </View>
        {/* Remounting stops the previous clip and resets playback and captions for the selection. */}
        <TutorialVideo
          key={video.slug}
          video={video}
          variant="lesson"
          onPlayStart={() => setRotating(false)}
          next={{ label: video.title, href: lessonHref, autoplay: false, action: 'Read the full lesson' }}
        />
        <Link href={lessonHref} asChild>
          <Pressable accessibilityRole="link" style={{ alignSelf: 'center', minHeight: 44, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            <Text weight="medium" style={{ color: c.ink, fontSize: 14 }}>Read the full lesson</Text>
            <Icon name="arrow-right" size={12} color={c.ink} />
          </Pressable>
        </Link>
      </View>
    </View>
  );
}

export default React.memo(HeroProductPreview);
